import { useEffect, useState, type FormEvent } from "react";
import type { Session } from "@supabase/supabase-js";
import { Link, useNavigate } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { supabase } from "@/integrations/supabase/client";
import { checkEmailAvailable } from "@/lib/email-check.functions";
import { getPendingUtm } from "@/lib/utmParams";
import { PasswordInput } from "@/knowmeno/PasswordInput";
import { buildDevAuthSession, isDevAuthBypassActive } from "@/knowmeno/devAuthBypass";

interface AuthGateProps {
  children: (session: Session) => React.ReactNode;
}

const RO = "#FF5C7A";
const GD = "#6E4B71";
const GR = "#6E4B71";
const T = "#FFC1CD";
const WH = "#ffffff";

/**
 * AuthGate: only renders children when there's a session.
 * If no session, redirects to /sign-in.
 */
export function AuthGate({ children }: AuthGateProps) {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    if (isDevAuthBypassActive()) {
      setSession(buildDevAuthSession());
      setLoading(false);
      return;
    }
    const { data: sub } = supabase.auth.onAuthStateChange((_event, s) => {
      setSession(s);
    });
    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session);
      setLoading(false);
    });
    return () => sub.subscription.unsubscribe();
  }, []);

  useEffect(() => {
    if (!loading && !session) {
      navigate({ to: "/sign-in" });
    }
  }, [loading, session, navigate]);

  if (loading || !session) {
    return (
      <div
        style={{
          maxWidth: 420,
          margin: "0 auto",
          minHeight: 400,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontFamily: "system-ui, sans-serif",
          color: GR,
          fontSize: 14,
        }}
      >
        Loading…
      </div>
    );
  }

  return <>{children(session)}</>;
}

interface AuthFormProps {
  mode: "signin" | "signup";
}

export function AuthForm({ mode }: AuthFormProps) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const navigate = useNavigate();
  const checkEmail = useServerFn(checkEmailAvailable);

  // If already signed in, bounce to /app
  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      if (data.session) navigate({ to: "/app" });
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_event, s) => {
      if (s) navigate({ to: "/app" });
    });
    return () => sub.subscription.unsubscribe();
  }, [navigate]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    setInfo(null);
    try {
      if (mode === "signup") {
        // Block Gmail +alias / dot-trick re-signups before creating the auth user.
        try {
          const { available } = await checkEmail({ data: { email } });
          if (!available) {
            throw new Error(
              "An account already exists with this email. Try logging in, or use a different email address.",
            );
          }
        } catch (checkErr: any) {
          // Only swallow network-y errors — propagate the duplicate message.
          if (checkErr?.message?.startsWith("An account already exists")) throw checkErr;
          console.warn("[signup] email-availability check failed", checkErr);
        }
        const utm = getPendingUtm();
        const { data, error: err } = await supabase.auth.signUp({
          email,
          password,
          options: {
            emailRedirectTo: `${window.location.origin}/app`,
            // Attach first-touch UTMs to user_metadata so they flow into
            // PostHog identify(), analytics queries, and any downstream
            // attribution reporting. Empty object when no UTMs were captured.
            data: Object.keys(utm).length > 0 ? { ...utm } : undefined,
          },
        });
        if (err) throw err;
        // NOTE: The welcome email is intentionally NOT sent here. It fires
        // from ProfileSync once onboarding writes life_stage, so the email
        // can use the correct score label ("Vitality Score" vs "Menopause
        // Score") based on the user's stage. See post-signup.functions.ts.
        // Free-forever flow: new signups go straight to /app. Upgrading to
        // Plus is optional and reachable from /choose-plan via in-app CTAs.
        if (data.session) {
          navigate({ to: "/app" });
        } else {
          const { error: signInErr } = await supabase.auth.signInWithPassword({ email, password });
          if (signInErr) throw signInErr;
          navigate({ to: "/app" });
        }
      } else {
        const { error: err } = await supabase.auth.signInWithPassword({ email, password });
        if (err) throw err;
        navigate({ to: "/app" });
      }
    } catch (err: any) {
      setError(err.message || "Something went wrong");
    } finally {
      setSubmitting(false);
    }
  };

  const isSignup = mode === "signup";

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "linear-gradient(165deg, #FAF7F5 0%, #FFE4EA 60%, #FFE4EA 100%)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "24px 16px",
        fontFamily: '"Cormorant Garamond", Georgia, serif',
      }}
    >
      <div
        style={{
          maxWidth: 440,
          width: "100%",
          background: WH,
          borderRadius: 24,
          padding: "36px 32px",
          boxShadow: "0 30px 80px -30px rgba(74, 25, 66, 0.25)",
        }}
      >
        <Link
          to="/"
          style={{
            color: GR,
            fontSize: 13,
            textDecoration: "none",
            fontFamily: "system-ui, sans-serif",
            display: "inline-block",
            marginBottom: 18,
          }}
        >
          ← Back to home
        </Link>
        <div
          style={{
            fontSize: 30,
            fontWeight: 600,
            letterSpacing: "-0.01em",
            display: "flex",
            alignItems: "baseline",
            marginBottom: 10,
            fontFamily: "system-ui, sans-serif",
          }}
        >
          <span style={{ color: "#4B1F4F" }}>Know</span>
          <span style={{ color: "#FF5C7A" }}>Meno</span>
        </div>
        <h1
          style={{
            fontSize: 34,
            lineHeight: 1.1,
            color: "#4B1F4F",
            marginBottom: 8,
            fontWeight: 500,
            letterSpacing: "-0.02em",
          }}
        >
          {isSignup ? "Create your free account" : "Welcome back"}
        </h1>
        <p
          style={{
            color: GR,
            fontSize: 15,
            lineHeight: 1.55,
            marginBottom: 26,
            fontFamily: "system-ui, sans-serif",
          }}
        >
          {isSignup
            ? "Free forever. No card required."
            : "Sign in to continue tracking."}
        </p>

        <form
          onSubmit={handleSubmit}
          style={{ display: "flex", flexDirection: "column", gap: 12, fontFamily: "system-ui, sans-serif" }}
        >
          <input
            type="email"
            required
            autoComplete="email"
            aria-label="Email address"
            placeholder="Email address"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            style={inputStyle}
          />
          <PasswordInput
            required
            minLength={6}
            autoComplete={isSignup ? "new-password" : "current-password"}
            aria-label="Password"
            placeholder={isSignup ? "Create a password (6+ characters)" : "Password"}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            style={inputStyle}
          />
          {error && <div style={{ color: RO, fontSize: 13 }}>{error}</div>}
          {info && (
            <div
              style={{
                color: T,
                fontSize: 13,
                background: "#FFE4EA",
                padding: "10px 12px",
                borderRadius: 12,
                lineHeight: 1.5,
              }}
            >
              {info}
            </div>
          )}
          {!isSignup && (
            <div style={{ textAlign: "right", marginTop: -4 }}>
              <Link
                to="/forgot-password"
                style={{
                  color: "#4B1F4F",
                  fontSize: 13,
                  textDecoration: "none",
                  fontFamily: "system-ui, sans-serif",
                }}
              >
                Forgot password?
              </Link>
            </div>
          )}
          <button
            type="submit"
            disabled={submitting}
            style={{
              background: "#4B1F4F",
              color: WH,
              border: "none",
              borderRadius: 14,
              padding: 15,
              fontSize: 15,
              fontWeight: 500,
              cursor: submitting ? "default" : "pointer",
              opacity: submitting ? 0.6 : 1,
              marginTop: 6,
              fontFamily: "system-ui, sans-serif",
            }}
          >
            {submitting ? "…" : isSignup ? "Create free account" : "Sign in"}
          </button>
        </form>

        <div
          style={{
            marginTop: 22,
            textAlign: "center",
            fontSize: 13,
            color: GR,
            fontFamily: "system-ui, sans-serif",
          }}
        >
          {isSignup ? (
            <>
              Already have an account?{" "}
              <Link to="/sign-in" style={{ color: RO, fontWeight: 500, textDecoration: "none" }}>
                Sign in
              </Link>
            </>
          ) : (
            <>
              New to KnowMeno?{" "}
              <Link to="/sign-up" style={{ color: RO, fontWeight: 500, textDecoration: "none" }}>
                Create a free account
              </Link>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  border: "1px solid #E8D5DA",
  borderRadius: 14,
  padding: "14px 16px",
  fontSize: 15,
  fontFamily: "inherit",
  outline: "none",
  background: WH,
};
